/**
 * Normalize a phone number to international format without '+'.
 * - Strips 'whatsapp:' prefix
 * - Strips leading '+'
 * - Converts Israeli local format (05x…) → 9725x…
 *
 * Mirrors backend clinic_whatsapp._normalize_phone().
 */
export function normalizePhone(phone: string): string {
  phone = phone.trim().replace(/^whatsapp:/, "").replace(/^\+/, "");
  if (phone.startsWith("0") && phone.length <= 11) {
    phone = "972" + phone.slice(1);
  }
  return phone;
}

/**
 * How a phone number is shown to staff: Israeli numbers in local form, everything else as
 * it is. +972541234567 -> 0541234567.
 *
 * This is a faithful port of the backend's patients.display_phone_number, which is why the
 * frontend needs it at all: patient_page runs every phone field through that function before
 * returning it, but get_patients_tasks does not, so the same patient's number arrives
 * formatted on one screen and international on the other. Applying it on display makes the
 * two agree whatever the source.
 *
 * Display only - a call or a WhatsApp send wants what normalizePhone produces.
 */
export function displayPhoneNumber(phone?: string | null): string {
  return normalizePhoneNumber(phone).replace("+972", "0");
}

/**
 * Mirrors backend patient_utils.normalize_phone_number: strips spaces and hyphens, adds the
 * country code a bare local number is missing, and collapses the +9720 that a local number
 * with its leading zero turns into.
 */
function normalizePhoneNumber(phone?: string | null): string {
  // Not in the backend original: patient phone fields never carry this prefix, but a value
  // that came from a WhatsApp conversation would otherwise be treated as a local number and
  // have +972 stuck in front of it.
  const input = (phone || "").trim().replace(/^whatsapp:/, "");
  if (!input) return "";

  let result = input.replace(/[\s-]/g, "");
  if (input.startsWith("972") || input.startsWith("44")) {
    result = "+" + result;
  }
  if (!result.startsWith("+")) {
    result = "+972" + result;
  }
  if (result.startsWith("+9720")) {
    result = "+972" + result.slice(5);
  }
  if (result === "+972") {
    result = "";
  }
  return result;
}
